import {
  type ReactNode,
  type RefObject,
  useId,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import styles from "./dropdown.module.css";

/** Above v2 datepicker popper (`10050`); below app-level full-screen overlays if raised later. */
const DROPDOWN_PANEL_Z_INDEX = 10060;

/**
 * Must match react-datepicker / react-onclickoutside so clicks on this portaled panel are not
 * treated as "outside" the calendar (otherwise month/year Select lists close the picker).
 */
const REACT_DATEPICKER_OUTSIDE_CLICK_IGNORE_CLASS =
  "react-datepicker-ignore-onclickoutside";

export type DropdownPlacement = "bottom" | "top";


export type DropdownPanelAlign = "start" | "end";

export interface DropdownProps {
  trigger: ReactNode;
  children: ReactNode;
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  id?: string;
  className?: string;
  panelClassName?: string;
  panelRef?: RefObject<HTMLDivElement | null>;
  placement?: DropdownPlacement;
  
  panelMinWidth?: number;
 
  panelAlign?: DropdownPanelAlign;

  shrinkWrap?: boolean;
}

export function Dropdown({
  trigger,
  children,
  open,
  id,
  className,
  panelClassName,
  panelRef,
  placement = "bottom",
  panelMinWidth,
  panelAlign = "start",
  shrinkWrap = false,
}: DropdownProps) {
  const reactId = useId();
  const rootId = id ?? `dropdown-${reactId.replace(/:/g, "")}`;
  const panelId = `${rootId}-panel`;

  const containerRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, height: 0 });

  const updateCoords = () => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCoords({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateCoords();

    const onReposition = () => {
      updateCoords();
    };
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open]);

  const margin = 8;
  const maxPanelW = typeof window !== "undefined" ? window.innerWidth - margin * 2 : 0;
  const targetW = Math.max(coords.width, panelMinWidth ?? 0);
  const panelWidth = maxPanelW > 0 ? Math.min(targetW, maxPanelW) : targetW;

  let left =
    panelAlign === "end"
      ? coords.left + coords.width - panelWidth
      : coords.left;
  if (typeof window !== "undefined") {
    left = Math.max(margin, Math.min(left, window.innerWidth - panelWidth - margin));
  }
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 0;
  const bottomTop = coords.top + coords.height + 4;
  const topBottom = viewportHeight - coords.top + 4;
  const availablePanelHeight =
    viewportHeight > 0
      ? Math.max(
          120,
          placement === "bottom"
            ? viewportHeight - bottomTop - margin
            : coords.top - margin,
        )
      : undefined;

  const panelStyles = {
    position: "fixed",
    zIndex: DROPDOWN_PANEL_Z_INDEX,
    left: `${left}px`,
    width: `${panelWidth}px`,
    boxSizing: "border-box",
    // Defense in depth: if anything clamps `width`, the explicit floor keeps options readable.
    ...(panelMinWidth != null ? { minWidth: `${panelMinWidth}px` } : {}),
    // shrinkWrap menus (e.g. header avatar) may grow to content; Select panels stay at trigger width.
    ...(shrinkWrap ? { minWidth: "max-content" } : {}),
    ...(availablePanelHeight != null
      ? { maxHeight: `${availablePanelHeight}px`, overflow: "hidden" }
      : {}),
    ...(placement === "bottom"
      ? { top: `${bottomTop}px` }
      : { bottom: `${topBottom}px` }),
  } as const;

  return (
    <div
      className={clsx(styles.root, shrinkWrap && styles.rootShrinkWrap, className)}
      id={rootId}
      ref={containerRef}
    >
      {trigger}
      {open && createPortal(
        <div
          ref={panelRef}
          id={panelId}
          className={clsx(
            styles.panel,
            panelClassName,
            REACT_DATEPICKER_OUTSIDE_CLICK_IGNORE_CLASS,
          )}
          style={panelStyles}
          data-open="true"
          data-placement={placement}
          role="presentation"
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>,
        document.body
      )}
    </div>
  );
}
